import { orgLocalDate } from "@njbsoft/utils";
import { enumParam, pageParam, textParam, uuidParam, yearParam } from "./search-params";

/** The letters list's page size: the server pages it, and `page` is an offset into pages of this. */
export const LETTERS_PAGE_SIZE = 25;

/** `/letters`' search: the two filters and the page, every one sent to the server. */
export interface LettersSearch {
  /** A facility id: the wire `facility_id`. */
  property?: string;
  /** A calendar year, 2000 to 9999: the wire `year`. */
  year?: number;
  /** A page past the first; the first page is the param's absence. */
  page?: number;
}

export type IssueStatusFilter = "open" | "resolved";

export const ISSUE_STATUS_FILTERS: readonly IssueStatusFilter[] = ["open", "resolved"];

/** `/letters/issues`' search, applied in the browser: the issues read is drained whole. */
export interface IssuesSearch {
  /** Matched inside the type's words, the property name, the serial or the details. */
  q?: string;
  status?: IssueStatusFilter;
}

const YEAR_MIN = 2000;

// The params the search does not hold are left out rather than carried as
// undefined, so the search reads as the URL does.
function withoutUndefined<T extends object>(value: T): T {
  // Dropping the undefined entries leaves every remaining key as `T` types it.
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}

/** Each param through its validator; anything else, and any value a param cannot hold, is dropped. */
export function validateLettersSearch(search: Record<string, unknown>): LettersSearch {
  return withoutUndefined<LettersSearch>({
    property: uuidParam(search.property),
    year: yearParam(search.year),
    page: pageParam(search.page),
  });
}

/** Each param through its validator; anything else, and any value a param cannot hold, is dropped. */
export function validateIssuesSearch(search: Record<string, unknown>): IssuesSearch {
  return withoutUndefined<IssuesSearch>({
    q: textParam(search.q),
    status: enumParam(search.status, ISSUE_STATUS_FILTERS),
  });
}

/** The calendar year `instant` falls in, read in `timezone` (UTC without one); null when unreadable. */
function yearIn(instant: Date, timezone: string | undefined): number | null {
  if (Number.isNaN(instant.getTime())) return null;
  return Number(orgLocalDate(instant, timezone ?? "UTC").slice(0, 4));
}

/**
 * The year filter's options, newest first: from the current year back to the
 * year of the oldest letter's `generated_at`, both read in the active
 * utility's `timezone`, the zone the server files each letter's year in. A
 * letter generated on December 31 in that zone, already January 1 in UTC,
 * counts in the earlier year. With no letter, the current year alone; no year
 * before 2000, which the server's filter refuses.
 */
export function letterYearOptions(
  oldestGeneratedAt: string | null,
  now: Date,
  timezone: string | undefined,
): number[] {
  const current = yearIn(now, timezone) ?? now.getUTCFullYear();
  const oldest = oldestGeneratedAt === null ? null : yearIn(new Date(oldestGeneratedAt), timezone);
  const first = Math.max(YEAR_MIN, Math.min(oldest ?? current, current));
  const years: number[] = [];
  for (let year = current; year >= first; year -= 1) years.push(year);
  return years;
}
